var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { PluginKey } from 'prosemirror-state';
import { filterData, groupData, toArray } from '../../libs';
import { createCtrlPlugin } from '../ctrl-plugin';
var transEvents = ['transformPastedHTML', 'transformPastedText', 'clipboardTextParser', 'transformPasted'];
var CUSTOM_CTRL_ACCEPT = {
    createSelectionBetween: true,
    domParser: true,
    clipboardParser: true,
    clipboardTextParser: true,
    nodeViews: true,
    clipboardSerializer: true,
    decorations: true,
    editable: true,
    attributes: true,
    scrollThreshold: true,
    scrollMargin: true,
    appendTransaction: true,
    eventHandler: true,
    filterTransaction: true,
};
// transEvents means only transport one parameter
var transEventChain = function (adapter, handles) {
    var content = [];
    for (var _i = 2; _i < arguments.length; _i++) {
        content[_i - 2] = arguments[_i];
    }
    if (!adapter.view.editable)
        return content[0];
    return handles.reduce(function (_res, handle) {
        var res = handle(adapter, _res);
        return res;
    }, content[0]);
};
var chainEvent = function (event, adapter, handlers) {
    var args = [];
    for (var _i = 3; _i < arguments.length; _i++) {
        args[_i - 3] = arguments[_i];
    }
    if (transEvents.includes(event)) {
        return transEventChain.apply(void 0, __spreadArray([adapter, handlers], args, false));
    }
    if (!adapter.view.editable)
        return false;
    return handlers.some(function (handler) { return handler.apply(void 0, __spreadArray([adapter], args, false)); });
};
var CustomCtrlCenter = /** @class */ (function () {
    function CustomCtrlCenter(adapter, configs) {
        var _this = this;
        this.appendTransactions = [];
        this.eventHandler = {};
        this.props = {};
        this.filterTransactions = [];
        // group `appendTransaction` handler
        this.handleAppendTransaction = function (trs, oldState, _newState) {
            var newState = _newState;
            var tr = newState.tr;
            var appended = false;
            _this.appendTransactions.forEach(function (handler) {
                var res = handler(tr, oldState, newState);
                if (res) {
                    newState = _newState.applyInner(res);
                    appended = true;
                }
            });
            if (appended)
                return tr;
        };
        // group `filterTransaction` handler
        this.handleFilterTransaction = function (tr, state) {
            var isPass = true;
            _this.filterTransactions.some(function (handler) {
                isPass = handler(tr, state);
                return !isPass;
            });
            return isPass;
        };
        this.defaultDomEventHandler = function (event) {
            return _this.adapter.view.someProp('handleDOMEvents', function (handlers) {
                var handler = handlers[event.type];
                return handler ? handler(_this.adapter.view, event) || event.defaultPrevented : false;
            });
        };
        this.ensureDOMEvents = function () {
            var view = _this.adapter.view;
            view.someProp('handleDOMEvents', function (currentHandlers) {
                var _a, _b;
                var nativeHandlers = (_a = view.eventHandlers) !== null && _a !== void 0 ? _a : (_b = view.input) === null || _b === void 0 ? void 0 : _b.eventHandlers;
                if (!nativeHandlers)
                    return;
                for (var type in currentHandlers) {
                    // eventHandlers was moving to input after prosemirror-view 1.24.0
                    if (!nativeHandlers[type]) {
                        view.dom.addEventListener(type, (nativeHandlers[type] = function (event) { return _this.defaultDomEventHandler(event); }));
                    }
                }
            });
        };
        // assigned `this.props` according to `eventHandler`
        this.handleProps = function () {
            Object.keys(_this.eventHandler).reduce(function (result, event) {
                var handler;
                // collect domEvent handlers and chain
                if (event === 'handleDOMEvents') {
                    handler = Object.keys(_this.eventHandler.handleDOMEvents).reduce(function (res, domEvent) {
                        res[domEvent] = function () {
                            var args = [];
                            for (var _i = 0; _i < arguments.length; _i++) {
                                args[_i] = arguments[_i];
                            }
                            return chainEvent.apply(void 0, __spreadArray([domEvent, _this.adapter, _this.eventHandler.handleDOMEvents[domEvent]], args, false));
                        };
                        return res;
                    }, {});
                }
                else {
                    // simple chain the custom event handler
                    handler = function () {
                        var args = [];
                        for (var _i = 0; _i < arguments.length; _i++) {
                            args[_i] = arguments[_i];
                        }
                        return chainEvent.apply(void 0, __spreadArray([event, _this.adapter, _this.eventHandler[event]], args, false));
                    };
                }
                result[event] = handler;
                return result;
            }, _this.props);
            _this.ensureDOMEvents();
        };
        this.register = function (registerConfigs, prioritized) {
            if (prioritized === void 0) { prioritized = false; }
            toArray(registerConfigs).forEach(function (config) {
                Object.keys(config).forEach(function (configName) {
                    if (configName === 'appendTransaction' && config.appendTransaction) {
                        _this.appendTransactions[prioritized ? 'unshift' : 'push'](config.appendTransaction);
                    }
                    else if (configName === 'filterTransaction' && config.filterTransaction) {
                        _this.filterTransactions[prioritized ? 'unshift' : 'push'](config.filterTransaction);
                    }
                    else if (configName === 'eventHandler') {
                        var eventHandler_1 = config.eventHandler;
                        if (!eventHandler_1)
                            return;
                        Object.keys(eventHandler_1).forEach(function (event) {
                            var handler = eventHandler_1[event];
                            if (!handler)
                                return;
                            if (event === 'handleDOMEvents') {
                                if (!_this.eventHandler.handleDOMEvents)
                                    _this.eventHandler.handleDOMEvents = {};
                                var domEventHandler_1 = handler;
                                Object.keys(domEventHandler_1).forEach(function (key) {
                                    if (!domEventHandler_1[key])
                                        return;
                                    groupData(_this.eventHandler.handleDOMEvents, key, domEventHandler_1[key], prioritized);
                                });
                            }
                            else {
                                groupData(_this.eventHandler, event, handler, prioritized);
                            }
                        });
                    }
                    else if (CUSTOM_CTRL_ACCEPT[configName]) {
                        // @ts-ignore
                        _this.props[configName] = config[configName];
                    }
                });
            });
            _this.handleProps();
        };
        this.unregister = function (registerConfigs) {
            toArray(registerConfigs).forEach(function (config) {
                Object.keys(config).forEach(function (configName) {
                    if (configName === 'appendTransaction') {
                        filterData(_this, 'appendTransactions', config.appendTransaction);
                    }
                    else if (configName === 'filterTransaction') {
                        filterData(_this, 'filterTransactions', config.filterTransaction);
                    }
                    else if (configName === 'eventHandler') {
                        Object.keys(config.eventHandler).forEach(function (key) {
                            if (key === 'handleDOMEvents') {
                                Object.keys(config.eventHandler.handleDOMEvents).forEach(function (key) {
                                    var _a;
                                    if (!((_a = _this.eventHandler.handleDOMEvents) === null || _a === void 0 ? void 0 : _a[key]))
                                        return;
                                    filterData(_this.eventHandler.handleDOMEvents, key, config.eventHandler.handleDOMEvents[key]);
                                });
                            }
                            else if (_this.eventHandler[key]) {
                                filterData(_this.eventHandler, key, config.eventHandler[key]);
                            }
                        });
                    }
                    else if (CUSTOM_CTRL_ACCEPT[configName]) {
                        // @ts-ignore
                        _this.props[configName] = undefined;
                    }
                });
            });
            _this.handleProps();
        };
        this.spec = {
            key: new PluginKey('customControl'),
            props: this.props,
            appendTransaction: this.handleAppendTransaction,
            filterTransaction: this.handleFilterTransaction,
        };
        this.adapter = adapter;
        if (configs)
            this.register(configs);
    }
    return CustomCtrlCenter;
}());
var createCustomCtrlPlugin = function (adapter, customProps) {
    var ctrlCenter = new CustomCtrlCenter(adapter, customProps);
    return createCtrlPlugin(ctrlCenter.spec, ctrlCenter);
};
export { createCustomCtrlPlugin, CUSTOM_CTRL_ACCEPT };
//# sourceMappingURL=index.js.map